import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, useNavigationType } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { titleCase } from "@/lib/format";
import { loginPath } from "@/lib/api";
import GlobalSearch from "@/components/GlobalSearch";
import { AlertsBell } from "@/components/AlertsBell";
import { PendingCallsBadge } from "@/components/CallTracker";
import { InstallApp } from "@/components/InstallApp";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  LayoutDashboard, Users, UserSquare2, FileSpreadsheet, FileCheck2, ShieldCheck,
  RefreshCw, Wallet, Percent, Receipt, Building2, BarChart3, UserCog, ScrollText,
  Search, Moon, Sun, LogOut, Menu, Command, LifeBuoy, Headset, Landmark, Building,
  Package, Settings2, ClipboardList, UploadCloud, ChevronDown, Megaphone, Coins, BadgeCheck, MapPin, PlugZap, Headphones, Gauge, LayoutTemplate, Trophy, Target, Lock,
} from "lucide-react";

const DASHBOARD = { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard:view", end: true };

const GROUPS = [
  { key: "calling", label: "Calling", icon: Headphones, items: [
    { to: "/workspace", label: "Calling Workspace", icon: Headphones, perm: "dashboard:view" },
    { to: "/customers", label: "Customers", icon: UserSquare2, perm: "customers:view" },
    { to: "/team-performance", label: "Team Performance", icon: Trophy, perm: "leads:assign" },
  ]},
  { key: "insurance", biz: "insurance", label: "Insurance", icon: ShieldCheck, items: [
    { to: "/leads", label: "Insurance Leads", icon: Users, perm: "leads:view" },
    { to: "/quotes", label: "Quotes", icon: FileSpreadsheet, perm: "quotes:view" },
    { to: "/proposals", label: "Proposals", icon: FileCheck2, perm: "proposals:view" },
    { to: "/policies", label: "Policies", icon: ShieldCheck, perm: "policies:view" },
    { to: "/renewals", label: "Renewals", icon: RefreshCw, perm: "renewals:view" },
    { to: "/claims", label: "Claims", icon: LifeBuoy, perm: "claims:view" },
    { to: "/tickets", label: "Service Tickets", icon: Headset, perm: "tickets:view" },
    { to: "/insurers", label: "Insurers", icon: Building2, perm: "insurers:view" },
  ]},
  { key: "loan", biz: "loan", label: "Loans", icon: Landmark, items: [
    { to: "/loans", label: "Loan Dashboard", icon: Landmark, perm: "loans:view" },
    { to: "/loan-leads", label: "Loan Leads", icon: Users, perm: "loans:view" },
    { to: "/loan-applications", label: "Applications", icon: FileCheck2, perm: "loans:view" },
    { to: "/loan-followups", label: "Loan Follow-ups", icon: ClipboardList, perm: "loans:view" },
    { to: "/lenders", label: "Lenders", icon: Building, perm: "lenders:view" },
    { to: "/loan-products", label: "Loan Products", icon: Package, perm: "loan_products:view" },
    { to: "/loan-forms", label: "Loan Form Builder", icon: LayoutTemplate, perm: "form_builder:manage" },
  ]},
  { key: "leadsetup", label: "Lead Setup", icon: Megaphone, items: [
    { to: "/lead-import", label: "Lead Import", icon: UploadCloud, perm: "leads:add" },
    { to: "/area-routing", label: "Area Routing", icon: MapPin, perm: "leads:assign" },
    { to: "/lead-scoring", label: "Lead Quality Rules", icon: Gauge, perm: "scoring:manage" },
    { to: "/campaign-costs", label: "Campaign Costs", icon: Target, perm: "campaign_costs:view" },
  ]},
  { key: "finance", label: "Finance & Accounts", icon: Wallet, items: [
    { to: "/payments", label: "Payments", icon: Wallet, perm: "payments:view", biz: "insurance" },
    { to: "/commissions", label: "Commissions", icon: Percent, perm: "commissions:view", biz: "insurance" },
    { to: "/loan-commissions", label: "Loan Commissions", icon: Coins, perm: "loan_commissions:view", biz: "loan" },
    { to: "/expenses", label: "Expenses", icon: Receipt, perm: "expenses:view" },
  ]},
  { key: "agents", label: "Agent Network", icon: BadgeCheck, items: [
    { to: "/agents", label: "Agents", icon: BadgeCheck, perm: "agents:view" },
    { to: "/agent-payouts", label: "Agent Payouts", icon: Coins, perm: "agents:payout" },
  ]},
  { key: "admin", label: "Reports & Admin", icon: Settings2, items: [
    { to: "/reports", label: "Reports", icon: BarChart3, perm: "reports:view" },
    { to: "/employee-sales", label: "Employee Sales", icon: Trophy, perm: "dashboard:view" },
    { to: "/users", label: "Users", icon: UserCog, perm: "users:password" },
    { to: "/settings", label: "Settings", icon: Settings2, perm: "*" },
    { to: "/integrations", label: "Integrations", icon: PlugZap, perm: "*" },
    { to: "/security", label: "Security", icon: Lock, perm: "dashboard:view" },
    { to: "/audit", label: "Audit Log", icon: ScrollText, perm: "audit:view" },
  ]},
];

const showBiz = (biz, ins, loan) => !biz || (biz === "insurance" ? ins : loan);

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("omi-theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("omi-theme", dark ? "dark" : "light");
  }, [dark]);
  return { dark, toggle: () => setDark((d) => !d) };
}

export default function Layout({ children }) {
  const { user, logout, hasPerm } = useAuth();
  const { dark, toggle } = useTheme();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();
  const navType = useNavigationType();

  const dept = hasPerm("*") ? "both" : (user?.department || "both");
  const showInsurance = dept === "both" || dept === "insurance";
  const showLoan = dept === "both" || dept === "loans";

  const visible = (items) => items.filter((n) => showBiz(n.biz, showInsurance, showLoan) && (n.perm === "*" ? hasPerm("*") : hasPerm(n.perm)));

  const groups = GROUPS
    .filter((g) => showBiz(g.biz, showInsurance, showLoan))
    .map((g) => ({ ...g, items: visible(g.items) }))
    .filter((g) => g.items.length > 0);

  // open the group that contains the active route (on load / route change)
  useEffect(() => {
    const active = groups.find((g) => g.items.some((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to))));
    if (active) setOpenKey(active.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  const showDashboard = hasPerm("dashboard:view");
  const goBack = () => (window.history.state?.idx > 1 ? nav(-1) : nav("/"));

  // keep one extra history entry so the phone's back gesture never closes the whole app
  useEffect(() => {
    if (window.history.state?.idx !== 0) return;
    if (navType === "POP") {
      const last = Number(sessionStorage.getItem("omi-exit-hint") || 0);
      if (window.innerWidth >= 1024 && !window.matchMedia("(display-mode: standalone)").matches) return;
      if (Date.now() - last < 2000) { sessionStorage.removeItem("omi-exit-hint"); window.history.back(); return; }
      sessionStorage.setItem("omi-exit-hint", String(Date.now()));
      toast("Press back again to exit", { id: "exit-hint", duration: 2000 });
    }
    nav(loc.pathname + loc.search, { state: { guard: true } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.key]);


  const navLink = (n) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.end}
      onClick={() => setMobileOpen(false)}
      data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 pl-9 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-300 hover:bg-slate-800 hover:text-white"
        }`
      }
    >
      <n.icon className="h-[16px] w-[16px] shrink-0" />
      {n.label}
    </NavLink>
  );

  const SidebarContent = (
    <>
      <div className="h-16 flex items-center gap-3 px-4 border-b border-slate-800">
        <div className="bg-white rounded-lg px-2 py-1 shrink-0" data-testid="sidebar-logo"><img src="/omi-logo.webp" alt="OMI Fintech Pvt Ltd" className="h-11 w-auto object-contain" /></div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 leading-tight">Insurance +<br />Loan CRM</div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
        {showDashboard && (
          <NavLink
            to={DASHBOARD.to}
            end
            onClick={() => setMobileOpen(false)}
            data-testid="nav-dashboard"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <LayoutDashboard className="h-[18px] w-[18px] shrink-0" />
            {DASHBOARD.label}
          </NavLink>
        )}

        {groups.map((g) => {
          const isOpen = openKey === g.key;
          return (
            <div key={g.key}>
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? null : g.key)}
                data-testid={`nav-group-${g.key}`}
                aria-expanded={isOpen}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  isOpen ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <g.icon className="h-[18px] w-[18px] shrink-0" />
                <span className="flex-1 text-left">{g.label}</span>
                <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && <div className="mt-0.5 space-y-0.5">{g.items.map(navLink)}</div>}
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-slate-700 text-white flex items-center justify-center text-sm font-semibold">
            {(user?.name || "U").charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-400 font-mono truncate">{user?.employee_id} · {titleCase(user?.role)}</div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 fixed left-0 top-0 bottom-0 z-40 bg-slate-900 dark:bg-slate-950 border-r border-slate-800">
        {SidebarContent}
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative flex flex-col w-64 bg-slate-900 dark:bg-slate-950">{SidebarContent}</aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="h-16 sticky top-0 z-30 bg-background/90 backdrop-blur-md border-b border-border px-4 sm:px-6 flex items-center justify-between gap-1.5 sm:gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            {loc.pathname !== "/" && <Button variant="ghost" size="icon" onClick={goBack} title="Back" data-testid="header-back-btn"><ArrowLeft className="h-5 w-5" /></Button>}
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="global-search-trigger"
              className="flex items-center gap-2 h-9 px-3 rounded-lg border border-border bg-muted/40 text-muted-foreground text-sm hover:bg-muted transition-colors w-9 sm:w-72 justify-center sm:justify-start px-0 sm:px-3"
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Search everything</span>
              
              <kbd className="ml-auto hidden sm:flex items-center gap-0.5 text-[10px] font-mono bg-background border border-border rounded px-1.5 py-0.5">
                <Command className="h-2.5 w-2.5" />K
              </kbd>
            </button>
          </div>
          <div className="flex items-center gap-0.5 sm:gap-1.5">
            <InstallApp className="h-9 px-2 sm:px-3" label="App" compact />
            <PendingCallsBadge />
            <AlertsBell />
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" title="Toggle theme">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={async () => { const p = loginPath(); await logout(); nav(p); }} data-testid="logout-btn" title="Logout">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 fade-in">{children}</main>
      </div>

      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
    </div>
  );
}
